import { protectedPage } from '../lib/page-runtime';
import {
  clearInlineStatus,
  renderAccountFacts,
  renderSettingsLoadError,
  renderSettingsLoadingState,
  setEmailStatusSummary,
  setInlineStatus,
  setSubmitButtonBusy,
} from '../lib/user-settings-panels';
import type { UserProfile, UserSettings } from '../types/api';

(function() {
  const form = document.getElementById('profile-form');
  if (!form) return;

  type SettingsSection = 'account' | 'profile' | 'notifications';
  type NotificationWatchSettings = Pick<UserSettings, 'watch_own_issues' | 'watch_own_submissions'>;
  interface ProfileFormValues {
    readonly display_name: string;
    readonly bio: string;
    readonly location: string;
    readonly website: string;
  }
  interface PasswordChangeValues {
    readonly oldPassword: string;
    readonly newPassword: string;
    readonly confirmPassword: string;
  }

  let originalData: UserProfile | null = null;
  let originalNotificationSettings: NotificationWatchSettings | null = null;
  let emailVerificationFocusApplied = false;
  let emailVerificationRefreshAvailable = false;
  let activeSettingsSection: SettingsSection = window.UserSettingsData.resolveSettingsSection(window.location.search);
  const activeSettingsTabClass = 'settings-tab rounded border border-brand-700 bg-brand-950/30 px-3 py-2 text-sm font-medium text-brand-200';
  const inactiveSettingsTabClass = 'settings-tab rounded border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-200';

  function reportInlineFailure(elementId: string, message: string): void {
    setInlineStatus(elementId, message, 'error');
    showToast(message, 'error');
  }

  function setNotificationSettingsFormEnabled(enabled: boolean): void {
    const notificationForm = document.getElementById('notification-settings-form') as HTMLFormElement | null;
    if (!notificationForm) return;
    notificationForm.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button:not(#notification-settings-retry)').forEach(function(control) {
      control.disabled = !enabled;
    });
    if (enabled) syncNotificationSettingsSubmitState();
  }

  function setNotificationSettingsRetryVisible(visible: boolean): void {
    const retryButton = document.getElementById('notification-settings-retry') as HTMLButtonElement | null;
    if (!retryButton) return;
    retryButton.style.display = visible ? '' : 'none';
    retryButton.setAttribute('aria-hidden', visible ? 'false' : 'true');
    retryButton.disabled = false;
    retryButton.removeAttribute('aria-busy');
  }

  function readProfileFormValues(): ProfileFormValues {
    return {
      display_name: (document.getElementById('display-name') as HTMLInputElement | null)?.value.trim() || '',
      bio: (document.getElementById('bio') as HTMLTextAreaElement | null)?.value.trim() || '',
      location: (document.getElementById('location') as HTMLInputElement | null)?.value.trim() || '',
      website: (document.getElementById('website') as HTMLInputElement | null)?.value.trim() || '',
    };
  }

  function syncProfileSubmitState(): void {
    const submitButton = document.getElementById('profile-submit') as HTMLButtonElement | null;
    if (!submitButton) return;
    const formState = window.UserSettingsData.buildProfileFormState(originalData, readProfileFormValues());
    submitButton.disabled = formState.submitDisabled;
    submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
  }

  function readNotificationWatchSettings(): NotificationWatchSettings {
    return {
      watch_own_issues: (document.getElementById('watch-own-issues') as HTMLInputElement | null)?.checked || false,
      watch_own_submissions: (document.getElementById('watch-own-submissions') as HTMLInputElement | null)?.checked || false,
    };
  }

  function syncNotificationSettingsSubmitState(): void {
    const submitButton = document.getElementById('notification-settings-submit') as HTMLButtonElement | null;
    if (!submitButton) return;
    const formState = window.UserSettingsData.buildNotificationSettingsFormState(
      originalNotificationSettings,
      readNotificationWatchSettings(),
    );
    submitButton.disabled = formState.submitDisabled;
    submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
  }

  function setSettingsSection(section: SettingsSection, options: { updateUrl?: boolean } = {}): void {
    activeSettingsSection = section;
    document.querySelectorAll<HTMLElement>('[data-settings-panel]').forEach(function(panel) {
      const selected = panel.dataset.settingsPanel === section;
      panel.style.display = selected ? '' : 'none';
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
    });
    document.querySelectorAll<HTMLButtonElement>('[data-settings-tab]').forEach(function(tab) {
      const selected = tab.dataset.settingsTab === section;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.className = selected ? activeSettingsTabClass : inactiveSettingsTabClass;
    });
    if (options.updateUrl) {
      const url = new URL(window.location.href);
      url.search = window.UserSettingsData.buildSettingsSectionSearch(url.search, section);
      window.history.replaceState(null, '', url);
    }
  }

  function initializeSettingsSectionNav(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-settings-tab]').forEach(function(tab) {
      tab.addEventListener('click', function() {
        const section = tab.dataset.settingsTab;
        if (section === 'account' || section === 'profile' || section === 'notifications') {
          setSettingsSection(section, { updateUrl: true });
        }
      });
    });
    setSettingsSection(activeSettingsSection);
  }

  function focusEmailVerificationIfRequested(verificationDisplay: ReturnType<typeof window.UserSettingsData.buildEmailVerificationDisplay>): void {
    if (emailVerificationFocusApplied || !window.UserSettingsData.shouldFocusEmailVerification(window.location.search)) return;
    emailVerificationFocusApplied = true;

    const alert = document.getElementById('email-verification-alert');
    const section = document.getElementById('account-email-section');
    const verifyBtn = document.getElementById('verify-email-btn') as HTMLButtonElement | null;
    const scrollTarget = verificationDisplay.alertVisible ? alert : section;
    scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (verificationDisplay.verifyButtonVisible && verifyBtn) {
      verifyBtn.focus({ preventScroll: true });
    }
    window.history.replaceState(
      null,
      '',
      window.UserSettingsData.consumeEmailVerificationFocusUrl(window.location.href),
    );
  }

  function applyEmailVerificationRequestFeedback(feedback: ReturnType<typeof window.UserSettingsData.buildEmailVerificationRequestFeedback>): void {
    const verificationAlertText = document.getElementById('email-verification-alert-text');
    if (verificationAlertText) {
      verificationAlertText.textContent = feedback.statusText;
    }
    const emailStatusSummary = document.getElementById('email-status-summary');
    if (emailStatusSummary) {
      setEmailStatusSummary(feedback.statusText, 'success');
    }
    const verifyBtn = document.getElementById('verify-email-btn') as HTMLButtonElement | null;
    if (verifyBtn) {
      verifyBtn.textContent = feedback.buttonText;
      verifyBtn.disabled = feedback.buttonDisabled;
      verifyBtn.removeAttribute('aria-busy');
    }
    emailVerificationRefreshAvailable = true;
    setEmailVerificationRefreshVisible(true);
  }

  function setEmailVerificationRefreshVisible(visible: boolean): void {
    const refreshBtn = document.getElementById('refresh-email-status-btn') as HTMLButtonElement | null;
    if (!refreshBtn) return;
    refreshBtn.style.display = visible ? '' : 'none';
    refreshBtn.disabled = false;
    refreshBtn.removeAttribute('aria-busy');
    refreshBtn.textContent = 'Refresh status';
  }

  function setEmailVerificationContinueVisible(profile: Pick<UserProfile, 'email_verified_at'>): void {
    const continueLink = document.getElementById('continue-after-verification-link') as HTMLAnchorElement | null;
    if (!continueLink) return;
    const returnTarget = window.UserSettingsData.getEmailVerificationReturnTarget(window.location.search);
    const visible = window.UserSettingsData.shouldShowEmailVerificationReturnAction(profile, returnTarget);
    continueLink.style.display = visible ? '' : 'none';
    if (returnTarget) {
      continueLink.href = returnTarget;
    }
  }

  function syncEmailPasswordRequirement(): void {
    const emailInput = document.getElementById('new-email') as HTMLInputElement | null;
    const passwordInput = document.getElementById('email-password') as HTMLInputElement | null;
    const passwordHelp = document.getElementById('email-password-help');
    const submitButton = document.getElementById('change-email-submit') as HTMLButtonElement | null;
    if (!emailInput || !passwordInput) return;
    const formState = window.UserSettingsData.buildEmailChangeFormState(
      originalData?.email || '',
      emailInput.value,
      passwordInput.value,
    );
    passwordInput.disabled = !formState.passwordRequired;
    passwordInput.setAttribute('aria-disabled', formState.passwordRequired ? 'false' : 'true');
    passwordInput.setAttribute('aria-required', formState.passwordRequired ? 'true' : 'false');
    if (!formState.passwordRequired) passwordInput.value = '';
    if (passwordHelp) {
      passwordHelp.textContent = formState.passwordHelpText;
    }
    if (submitButton) {
      submitButton.disabled = formState.submitDisabled;
      submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
    }
  }

  function readPasswordChangeValues(): PasswordChangeValues {
    return {
      oldPassword: (document.getElementById('old-password') as HTMLInputElement | null)?.value || '',
      newPassword: (document.getElementById('new-password') as HTMLInputElement | null)?.value || '',
      confirmPassword: (document.getElementById('confirm-password') as HTMLInputElement | null)?.value || '',
    };
  }

  function syncPasswordChangeSubmitState(): void {
    const submitButton = document.getElementById('change-password-submit') as HTMLButtonElement | null;
    const submitHelp = document.getElementById('change-password-submit-help') as HTMLDivElement | null;
    if (!submitButton) return;
    const formValues = readPasswordChangeValues();
    const formState = window.UserSettingsData.buildPasswordChangeFormState(formValues);
    const helpText = window.UserSettingsData.buildPasswordChangeFormHelpText(formValues);
    submitButton.disabled = formState.submitDisabled;
    submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
    if (submitHelp) {
      submitHelp.textContent = helpText;
      submitHelp.classList.toggle('hidden', helpText.length === 0);
    }
  }

  function handleEmailChangeInput(): void {
    clearInlineStatus('change-email-status');
    syncEmailPasswordRequirement();
  }

  function handlePasswordChangeInput(): void {
    clearInlineStatus('change-password-status');
    syncPasswordChangeSubmitState();
  }

  async function loadSettings() {
    try {
      const result = await window.UserSettingsData.loadSettingsData();

      if (!result.ok) {
        const message = result.error || 'Failed to load settings';
        renderSettingsLoadError(message, loadSettings);
        showToast(message, 'error');
        return;
      }

      const { profile, notificationSettings } = result.data;
      originalData = profile;
      syncEmailPasswordRequirement();

      // Populate form
      (document.getElementById('username') as HTMLInputElement).value = profile.username;
      (document.getElementById('display-name') as HTMLInputElement).value = profile.display_name || '';
      (document.getElementById('bio') as HTMLTextAreaElement).value = profile.bio || '';
      (document.getElementById('location') as HTMLInputElement).value = profile.location || '';
      (document.getElementById('website') as HTMLInputElement).value = profile.website || '';
      syncProfileSubmitState();
      const profileLink = document.getElementById('view-profile-link') as HTMLAnchorElement | null;
      if (profileLink) {
        profileLink.href = `/users/${encodeURIComponent(profile.username)}`;
      }

      // Account info
      const verificationDisplay = window.UserSettingsData.buildEmailVerificationDisplay(profile);
      renderAccountFacts(profile, verificationDisplay);
      if (profile.email_verified_at) emailVerificationRefreshAvailable = false;
      setEmailVerificationRefreshVisible(emailVerificationRefreshAvailable && verificationDisplay.verifyButtonVisible);
      setEmailVerificationContinueVisible(profile);
      if (
        !profile.email_verified_at
        && verificationDisplay.verifyButtonVisible
        && window.UserSettingsData.hasEmailVerificationSentHint(window.location.search)
      ) {
        applyEmailVerificationRequestFeedback(window.UserSettingsData.buildEmailVerificationRequestFeedback({}));
      }

      if (notificationSettings) {
        const s = notificationSettings;
        originalNotificationSettings = {
          watch_own_issues: s.watch_own_issues,
          watch_own_submissions: s.watch_own_submissions,
        };
        (document.getElementById('watch-own-issues') as HTMLInputElement).checked = s.watch_own_issues;
        (document.getElementById('watch-own-submissions') as HTMLInputElement).checked = s.watch_own_submissions;
        setNotificationSettingsFormEnabled(true);
        setNotificationSettingsRetryVisible(false);
        clearInlineStatus('notification-settings-status');
      } else {
        originalNotificationSettings = null;
        setNotificationSettingsFormEnabled(false);
        setNotificationSettingsRetryVisible(true);
        setInlineStatus(
          'notification-settings-status',
          'Notification settings could not be loaded. Retry before changing notification preferences.',
          'error',
        );
      }

      // Show form
      setSettingsSection(activeSettingsSection);
      document.getElementById('settings-loading')!.style.display = 'none';
      document.getElementById('settings-form')!.style.display = '';
      focusEmailVerificationIfRequested(verificationDisplay);
    } catch (e) {
      console.error('Failed to load settings:', e);
      renderSettingsLoadError('Network error while loading settings. Please try again.', loadSettings);
      showToast('Network error', 'error');
    }
  }

  form.addEventListener('submit', async function(e) {
    e.preventDefault();

    if (!originalData) return;

    const updates = window.UserSettingsData.buildProfileUpdates(originalData, readProfileFormValues());

    if (Object.keys(updates).length === 0) {
      setInlineStatus('profile-status', 'No profile changes to save.', 'info');
      showToast('No changes to save', 'info');
      return;
    }

    clearInlineStatus('profile-status');
    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    const restoreSubmitButton = setSubmitButtonBusy(submitButton, 'Saving...');
    try {
      const result = await window.UserSettingsData.updateProfileSettings(updates);

      if (result.ok) {
        originalData = result.data;
        setInlineStatus('profile-status', 'Profile updated.');
        showToast('Profile updated.');
      } else {
        reportInlineFailure('profile-status', result.error || 'Failed to update profile');
      }
    } catch (e) {
      console.error('Failed to update profile:', e);
      reportInlineFailure('profile-status', 'Network error');
    } finally {
      restoreSubmitButton();
      syncProfileSubmitState();
    }
  });

  document.getElementById('verify-email-btn')?.addEventListener('click', async function() {
    const btn = this as HTMLButtonElement;
    let restoreButton = true;
    const restoreSubmitButton = setSubmitButtonBusy(btn, 'Sending...');
    try {
      const result = await window.UserSettingsData.requestEmailVerification(window.location.search);
      if (result.ok) {
        const feedback = window.UserSettingsData.buildEmailVerificationRequestFeedback(result.data);
        showToast(feedback.toastText);
        if (feedback.reloadSettings) {
          await loadSettings();
          restoreButton = false;
          return;
        }
        applyEmailVerificationRequestFeedback(feedback);
        restoreButton = false;
      } else {
        setEmailStatusSummary(result.error || 'Failed to send verification email', 'error');
        showToast(result.error || 'Failed to send verification email', 'error');
      }
    } catch (e) {
      setEmailStatusSummary('Network error. Please try again.', 'error');
      showToast('Network error', 'error');
    } finally {
      if (restoreButton) {
        restoreSubmitButton();
      }
    }
  });

  document.getElementById('refresh-email-status-btn')?.addEventListener('click', async function() {
    const btn = this as HTMLButtonElement;
    const restoreSubmitButton = setSubmitButtonBusy(btn, 'Refreshing...');
    try {
      await loadSettings();
    } finally {
      restoreSubmitButton();
    }
  });

  document.getElementById('notification-settings-retry')?.addEventListener('click', async function() {
    const btn = this as HTMLButtonElement;
    const restoreSubmitButton = setSubmitButtonBusy(btn, 'Retrying...');
    try {
      await loadSettings();
    } finally {
      restoreSubmitButton();
    }
  });

  document.getElementById('change-email-form')?.addEventListener('submit', async function(e) {
    e.preventDefault();
    const form = this as HTMLFormElement;
    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const emailInput = document.getElementById('new-email') as HTMLInputElement;
    const passwordInput = document.getElementById('email-password') as HTMLInputElement;
    const requestedEmail = emailInput.value.trim();
    syncEmailPasswordRequirement();
    const validationError = window.UserSettingsData.validateEmailChangeInput({
      currentEmail: originalData?.email || '',
      newEmail: requestedEmail,
      password: passwordInput.value,
    });
    if (validationError) {
      reportInlineFailure('change-email-status', validationError);
      return;
    }
    if (originalData && window.UserSettingsData.isEmailChangeUnchanged(originalData.email, requestedEmail)) {
      const feedback = window.UserSettingsData.buildChangeEmailSuccessFeedback({
        email: originalData.email,
        unchanged: true,
        verification_email_sent: false,
      });
      setInlineStatus('change-email-status', feedback.toastText, 'info');
      showToast(feedback.toastText);
      emailInput.value = '';
      passwordInput.value = '';
      syncEmailPasswordRequirement();
      return;
    }
    clearInlineStatus('change-email-status');
    const restoreSubmitButton = setSubmitButtonBusy(btn, 'Updating email...');
    try {
      const result = await window.UserSettingsData.changeEmail({
        email: requestedEmail,
        password: passwordInput.value,
      }, window.location.search);
      if (result.ok) {
        const feedback = window.UserSettingsData.buildChangeEmailSuccessFeedback(result.data);
        setInlineStatus('change-email-status', feedback.toastText);
        showToast(feedback.toastText);
        emailInput.value = '';
        passwordInput.value = '';
        syncEmailPasswordRequirement();
        await loadSettings();
        if (feedback.verificationFeedback) {
          applyEmailVerificationRequestFeedback(feedback.verificationFeedback);
        }
      } else {
        reportInlineFailure('change-email-status', result.error || 'Failed to update email');
      }
    } catch (e) {
      reportInlineFailure('change-email-status', 'Network error');
    } finally {
      restoreSubmitButton();
      syncEmailPasswordRequirement();
    }
  });

  document.getElementById('change-password-form')?.addEventListener('submit', async function(e) {
    e.preventDefault();
    const form = this as HTMLFormElement;
    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const oldPasswordInput = document.getElementById('old-password') as HTMLInputElement;
    const newPasswordInput = document.getElementById('new-password') as HTMLInputElement;
    const confirmPasswordInput = document.getElementById('confirm-password') as HTMLInputElement;
    const validationError = window.UserSettingsData.validatePasswordChangeInput({
      oldPassword: oldPasswordInput.value,
      newPassword: newPasswordInput.value,
      confirmPassword: confirmPasswordInput.value,
    });
    if (validationError) {
      reportInlineFailure('change-password-status', validationError);
      return;
    }
    clearInlineStatus('change-password-status');
    const restoreSubmitButton = setSubmitButtonBusy(btn, 'Updating password...');
    try {
      const result = await window.UserSettingsData.changePassword({
        old_password: oldPasswordInput.value,
        new_password: newPasswordInput.value,
      });
      if (result.ok) {
        const feedback = window.UserSettingsData.buildPasswordChangeSuccessFeedback(result.data);
        setInlineStatus('change-password-status', feedback.statusText);
        showToast(feedback.toastText);
        oldPasswordInput.value = '';
        newPasswordInput.value = '';
        confirmPasswordInput.value = '';
        syncPasswordChangeSubmitState();
      } else {
        reportInlineFailure('change-password-status', result.error || 'Failed to update password');
      }
    } catch (e) {
      reportInlineFailure('change-password-status', 'Network error');
    } finally {
      restoreSubmitButton();
      syncPasswordChangeSubmitState();
    }
  });

  document.getElementById('notification-settings-form')?.addEventListener('submit', async function(e) {
    e.preventDefault();
    const form = this as HTMLFormElement;
    if (!originalNotificationSettings) {
      setInlineStatus(
        'notification-settings-status',
        'Notification settings are not ready. Use Retry before saving.',
        'error',
      );
      showToast('Retry loading notification settings before saving.', 'error');
      return;
    }

    const currentSettings = readNotificationWatchSettings();
    const updates = window.UserSettingsData.buildNotificationSettingsUpdates(originalNotificationSettings, currentSettings);
    if (!updates) {
      setInlineStatus('notification-settings-status', 'No notification changes to save.', 'info');
      showToast('No changes to save', 'info');
      return;
    }

    clearInlineStatus('notification-settings-status');
    const restoreSubmitButton = setSubmitButtonBusy(
      form.querySelector('button[type="submit"]') as HTMLButtonElement | null,
      'Saving...',
    );
    try {
      const result = await window.UserSettingsData.updateNotificationSettings(updates);
      if (result.ok) {
        originalNotificationSettings = currentSettings;
        setInlineStatus('notification-settings-status', 'Notification settings saved.');
        showToast('Notification settings saved');
      } else {
        reportInlineFailure('notification-settings-status', result.error || 'Failed to save notification settings');
      }
    } catch (e) {
      console.error('Failed to save notification settings:', e);
      reportInlineFailure('notification-settings-status', 'Network error');
    } finally {
      restoreSubmitButton();
      syncNotificationSettingsSubmitState();
    }
  });

  initializeSettingsSectionNav();
  ['display-name', 'bio', 'location', 'website'].forEach(function(id) {
    document.getElementById(id)?.addEventListener('input', function() {
      clearInlineStatus('profile-status');
      syncProfileSubmitState();
    });
  });
  ['new-email', 'email-password'].forEach(function(id) {
    document.getElementById(id)?.addEventListener('input', handleEmailChangeInput);
  });
  ['old-password', 'new-password', 'confirm-password'].forEach(function(id) {
    document.getElementById(id)?.addEventListener('input', handlePasswordChangeInput);
  });
  ['watch-own-issues', 'watch-own-submissions'].forEach(function(id) {
    document.getElementById(id)?.addEventListener('change', function() {
      clearInlineStatus('notification-settings-status');
      syncNotificationSettingsSubmitState();
    });
  });
  syncProfileSubmitState();
  syncPasswordChangeSubmitState();
  syncNotificationSettingsSubmitState();

  protectedPage({
    redirectTo: 'current',
    init: loadSettings,
  });
})();
